import React, { FC, useContext, useEffect, useState } from 'react';

// Material UI
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import {
  Button,
  Divider,
  Grid,
  Paper,
  Typography,
  CircularProgress,
  Box,
  FormControl,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  IconButton,
  DialogActions,
} from '@material-ui/core';

// interfaces and types
import { Props } from './interfaces';
// icons
import {
  mdiAccountArrowLeftOutline,
  mdiAccountOutline,
  mdiArrowRight,
  mdiCalendar,
  mdiClose,
  mdiStateMachine,
  mdiWarehouse,
} from '@mdi/js';
import Icon from '@mdi/react';

// components
import List from './components/list';
import jMoment from 'moment-jalaali';
import Can from '../../components/Can';
import AuthContext from '../../authContext';
import {
  removeEmptyValueObject,
  translate,
} from '../../utils/helpers';

import Pagination from '@material-ui/lab/Pagination';
import handleError from '../../utils/handleError';
import handleNotfication from '../../utils/handleNotification';
import usersRepository from 'client-sdk/src/Repository/users-repository';
import ordersRepository from 'client-sdk/src/Repository/orders-repository';
import cancellationTasksRepository from 'client-sdk/src/Repository/cancellation-task-repository';

// Style
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      '& > *': {
        '@media (max-width: 768px)': {
          margin: theme.spacing(0),
        },
      },
    },
    paper: {
      marginTop: '36px',
      marginBottom: '36px',
      paddingTop: theme.spacing(3),
      paddingBottom: theme.spacing(3),
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
      border: '1px solid #ddd',
      borderRadius: '4px',
    },

    item: {
      display: 'flex',
      alignSelf: 'center',
      alignItems: 'center',
      fontSize: '14px',
      fontWeight: 500,
    },
    icon: {
      marginRight: theme.spacing(1),
    },
    rtlIcon: {
      marginRight: theme.spacing(1),
      transform: 'scale(-1, 1)',
    },
    divider: {
      width: '98%',
      margin: '10px auto',
    },
    label: {
      display: 'flex',
      alignItems: 'center',
      fontSize: '14px',
      fontWeight: 400,
      color: '#8A8A8A',
      marginRight: '10px',
    },
    headerBox: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: '28px',
    },
    contentLabelTitle: {
      position: 'relative',
      color: theme.palette.gray.light,
      marginLeft: '40px',
      fontSize: '14px',
      fontWeight: 500,
      '&:before': {
        content: '"-"',
        position: 'absolute',
        left: '-20px',
      },
    },
    loading: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      height: '50vh',
      alignSelf: 'center',
      justifyContent: 'center',
      background: '#ccc',
    },
    changStatusIcon: {
      marginRight: '10px',
      position: 'absolute',
    },
    changStatusBox: {
      display: 'flex',
      textAlign: 'left',

      fontWeight: 400,
      fontSize: '14px',
    },
    renderValue: {
      paddingLeft: '32px',
    },
    radioGroup: {
      margin: '-8px 0',
    },
    radioGroupItem: {
      borderBottom: '1px solid #D8D8D8',
      marginRight: '-8px',
      padding: '8px 16px',
      '&:last-child': {
        borderBottom: '0px',
      },
    },
    cancelTitle: {
      fontSize: '16px',
      fontWeight: 500,
      textAlign: 'justify',
      lineHeight: 2,
      marginBottom: theme.spacing(3),
    },
    actionsBox: {
      paddingTop: theme.spacing(2),
      paddingRight: theme.spacing(3),
      paddingLeft: theme.spacing(3),
    },
    editIcon: {
      textAlign: 'center',
      position: 'relative',
      paddingLeft: theme.spacing(8),
      marginBottom: theme.spacing(7),
      '&::before': {
        content: '""',
        position: 'absolute',
        width: '100px',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%,-50%)',
        height: '100px',
        borderRadius: '50%',
        opacity: '0.2',
        backgroundColor: '#b297a7',
      },
    },
  })
);

const AssignOrder: FC<Props> = (props: Props): React.ReactElement => {
  const classes = useStyles();
  const authContext = useContext(AuthContext);
  const { id } = props.match.params;
  const [filterValues, setFilterValues] = useState({
    role: { equals: 'WAREHOUSE_OPERATOR' },
    with: 'orders-count',
    page: 0,
    size: 10,
  });
  const [openSize, setOpenSize] = useState(false as boolean);
  const [selectedOperator, setSelectedOperator] = useState(0);
  const [openCheckAssign, setOpenCheckAssign] = useState(false as boolean);
  const [order, setOrder] = useState({});
  const [users, setUsers] = useState([]);
  const [count, setCount] = useState<number>(0);
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [orderLoading, setOrderLoading] = useState<boolean>(false);

  const handleSelectOperator = (event: { target: HTMLInputElement }) => {
    const { target } = event;
    setSelectedOperator(+target.value);
  };

  const handleClose = () => {
    setOpenSize(false);
  };

  const handleChangeSizePage = (event: React.ChangeEvent<{ value: unknown }>) => {
    setFilterValues({
      ...filterValues,
      page: 0,
      size: Number(event.target.value),
    });
  };

  const handleChangePage = (
    event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    setFilterValues({ ...filterValues, page: page - 1 });
  };
  const getOrderWithId = async (id: any) => {
    const payload = { id };
    try {
      setOrderLoading(true);
      const response = await ordersRepository.getOrderWithId({ payload });
      setOrder(response.data);
    } catch (error) {
      setError(error.message);
      handleError(error.response);
    } finally {
      setOrderLoading(false);
    }
  };
  useEffect(() => {
    getOrderWithId(id);
    fetchUsers();
  }, [filterValues]);
  const getUsers = async (actions: any) => {
    const payload = { ...actions };
    try {
      setLoading(true);
      const { response, count } = await usersRepository.getUsers({ payload });
      setUsers(response.data);
      setCount(count.data);
    } catch (error) {
      setError(error.message);
      handleError(error.response);
    } finally {
      setLoading(false);
    }
  };
  const fetchUsers = () => {
    const { page, size } = filterValues;
    getUsers({
      ...removeEmptyValueObject(filterValues),
      page,
      size,
    });

    if (openCheckAssign) {
      setOpenCheckAssign(false);
      setSelectedOperator(0);
    }
  };

  const handleCloseCheckAssign = () => {
    setOpenCheckAssign(false);
  };
  const assignOrders = async (actions: any) => {
    const payload = { ...actions };
    try {
      const { pagination, callback } =
        await cancellationTasksRepository.updateCancellationTaskOperator({
          payload,
        });
      handleNotfication('سفارش با موفقیت تخصیص یافت', 'success');
      if (callback) {
        callback();
      }
    } catch (error) {
      setError(error.message);
      handleError(error.response);
    }
  };
  const handleSubmitAssign = () => {
    assignOrders({
      id,
      body: Number(selectedOperator),
      callback: fetchUsers(),
    });
  };

  return (
    <div className={classes.root}>
      <div className={classes.headerBox}>
        <Typography variant="h6" component="h2">
          اختصاص سفارش
        </Typography>
        <span className={classes.contentLabelTitle}>{id}</span>
      </div>
      <Button
        aria-label="back"
        size="medium"
        onClick={() => window.history.back()}
      >
        <Icon path={mdiArrowRight} size={1} />
        بازگشت
      </Button>
      <Paper elevation={3} className={classes.paper} variant="elevation" square>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={3}>
            <div className={classes.item}>
              <span className={classes.label}>
                <Icon
                  path={mdiAccountOutline}
                  size={1}
                  className={classes.icon}
                />
                نام سفارش‌دهنده:
              </span>

              <span>{order.customerName}</span>
            </div>
          </Grid>

          <Grid item xs={12} sm={3}>
            <div className={classes.item}>
              <span className={classes.label}>
                <Icon path={mdiWarehouse} size={1} className={classes.icon} />
                نام شرکت:
              </span>

              <span>{order?.partnerName}</span>
            </div>
          </Grid>

          <Grid item xs={12} sm={3}>
            <div className={classes.item}>
              <span className={classes.label}>
                <Icon path={mdiCalendar} size={1} className={classes.icon} />
                تعداد آیتم‌های سفارش:
              </span>
              <span>{order?.items?.length}</span>
            </div>
          </Grid>
          <Grid item xs={12} sm={3}>
            <div className={classes.item}>
              <span className={classes.label}>
                <Icon path={mdiCalendar} size={1} className={classes.icon} />
                تاریخ ثبت سفارش :
              </span>
              <span>
                {order.registrationDate &&
                  `${jMoment(order.registrationDate, 'YYYY/MM/DD')
                    .locale('fa')
                    .format('jYYYY/jMM/jDD')}`}
              </span>
            </div>
          </Grid>
          <Divider className={classes.divider} />
          <Grid item xs={12} sm={4}>
            <div className={classes.item}>
              <span className={classes.label}>
                <Icon
                  path={mdiStateMachine}
                  size={1}
                  className={classes.icon}
                />
                وضعیت تحویل :
              </span>
              <span>{translate(order.status, 'order')}</span>
            </div>
          </Grid>
        </Grid>
      </Paper>

      <Grid container justify={'flex-end'} spacing={3}>
        <Grid item>
          <Can
            role={authContext.user.role}
            perform="order:assign"
            yes={() => (
              <Button
                variant="contained"
                disabled={!selectedOperator}
                color="secondary"
                size="large"
                startIcon={<Icon path={mdiAccountArrowLeftOutline} size={1} />}
                onClick={() => {
                  setOpenCheckAssign(true);
                }}
              >
                اختصاص سفارش
              </Button>
            )}
          />
        </Grid>
      </Grid>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          {loading ? (
            <div className={classes.loading}>
              <CircularProgress />
            </div>
          ) : (
            <List
              onSelectOperator={handleSelectOperator}
              selectedOperator={selectedOperator}
              data={users}
            />
          )}
        </Grid>
        <Box mt={3} />
        {users.length > 0 && (
          <Grid container>
            <Grid item xs={12} sm={6}>
              <FormControl
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                }}
              >
                <div style={{ marginLeft: '10px', color: '#8a8a8a' }}>
                  {' '}
                  تعداد نمایش در هر صفحه:
                </div>
                <Select
                  open={openSize}
                  onClose={handleClose}
                  onOpen={() => setOpenSize(true)}
                  value={filterValues.size}
                  onChange={handleChangeSizePage}
                >
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={20}>20</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid xs={12} sm={6} container item direction="row-reverse">
              <Pagination
                count={Math.ceil(count / filterValues.size)}
                color="secondary"
                page={filterValues.page + 1}
                onChange={handleChangePage}
              />
            </Grid>
          </Grid>
        )}
      </Grid>

      <Dialog
        fullWidth
        open={openCheckAssign}
        onClose={handleCloseCheckAssign}
        aria-labelledby="responsive-dialog-title"
      >
        <DialogTitle>
          <div
            style={{
              position: 'absolute',
              left: '17px',
              zIndex: 10,
            }}
          >
            <IconButton aria-label="close" onClick={handleCloseCheckAssign}>
              <Icon path={mdiClose} size={1} />
            </IconButton>
          </div>
          <div className={classes.editIcon}>
            <Icon path={mdiAccountArrowLeftOutline} size={4} color="#b297a7" />
          </div>
          <div className={classes.cancelTitle}>
            {`
              آیا از اختصاص سفارش ${id} به اپراتور ${users.find(user => user.id === +selectedOperator)?.name
              } با کد ${selectedOperator && selectedOperator} اطمینان دارید؟
            `}
          </div>
        </DialogTitle>
        <Divider />
        <DialogActions>
          <Grid container spacing={1} justifyContent="flex-end">
            <Grid item xs={2}>
              <Button
                fullWidth
                autoFocus
                onClick={handleCloseCheckAssign}
                variant="outlined"
                size="large"
              >
                {'انصراف'}
              </Button>
            </Grid>
            <Grid item xs={4}>
              <Button
                onClick={handleSubmitAssign}
                fullWidth
                variant="contained"
                size="large"
                color="primary"
                disabled={orderLoading}
              >
                {orderLoading ? (
                  <CircularProgress size={24} color="secondary" />
                ) : (
                  'تخصیص سفارش'
                )}
              </Button>
            </Grid>
          </Grid>
        </DialogActions>
      </Dialog>
    </div>
  );
};

// const mapStateToProps = createStructuredSelector<Store, SelectorProps>({
//   order: makeSelectOrder(),
//   loading: makeSelectLoading(),
//   orderLoading: makeSelectOrderLoading(),
//   count: makeSelectCountList(),
//   users: makeSelectUsersList(),
//   error: makeSelectError(),
// });

// function mapDispatchToProps(dispatch: Function) {
//   return {
//     orderWithIdActionDispatch: (id: number) =>
//       dispatch(getOrderWithIdAction({id})),
//     getUsersActionDispatch: (payload: Payload) =>
//       dispatch(getUsersAction(payload)),
//     assignOrderActionDispatch: (payload: AssignPayload) =>
//       dispatch(assignOrderAction(payload)),
//   };
// }

// const withConnect = connect(mapStateToProps, mapDispatchToProps);
// // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
// // @ts-ignore
export default AssignOrder;
